import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { VoiceRecorder } from "capacitor-voice-recorder";
import {
	type ChangeEvent,
	type FormEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAnalytics } from "../../lib/analytics";
import { api } from "../../lib/api";
import { useClientTranslations } from "../../lib/i18n/client";
import styles from "./ChatInput.module.css";
import {
	ALLOWED_FILE_EXTENSIONS,
	AUDIO_MIME_TYPE,
	FILE_TYPE_ERROR_MESSAGE,
	TEXTAREA_MAX_HEIGHT,
} from "./constants";
import { FilePreview } from "./FilePreview";
import {
	convertFilesToDataUrls,
	removeFileFromFileList,
	validateFileTypes,
} from "./helpers";
import { MicIcon, PlusIcon, SparklesIcon } from "./icons";

interface ChatInputProps {
	input: string;
	setInput: (value: string) => void;
	handleInputChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
	handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
	isLoading: boolean;
	fileDataUrls?: Array<{ file: File; dataUrl: string }>;
	setFileDataUrls?: (urls: Array<{ file: File; dataUrl: string }>) => void;
	onSuggestClick?: () => void;
	onFocus?: () => void;
	onBlur?: () => void;
	isExpanded?: boolean;
}

export function ChatInput({
	input,
	setInput,
	handleInputChange,
	handleSubmit,
	isLoading,
	fileDataUrls = [],
	setFileDataUrls,
	onSuggestClick,
	onFocus,
	onBlur,
	isExpanded = false,
}: ChatInputProps) {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const attachMenuRef = useRef<HTMLDivElement>(null);
	const { t } = useClientTranslations("common");
	const { t: tChat } = useClientTranslations("chat");
	const { logChat } = useAnalytics();
	const [isRecording, setIsRecording] = useState(false);
	const [isTranscribing, setIsTranscribing] = useState(false);
	const [files, setFiles] = useState<FileList | undefined>(undefined);
	const [showAttachMenu, setShowAttachMenu] = useState(false);
	const isNativePlatform = Capacitor.isNativePlatform();
	const navigate = useNavigate();

	// Start a fresh Eve conversation (86ey1fqaa). ChatPage reads ?new=true and
	// provisions a new chat via useTodayChat({ forceNew: true }).
	const handleNewConversation = useCallback(() => {
		setShowAttachMenu(false);
		logChat("new_conversation_clicked", { source: "chat_input_menu" });
		navigate("/chat?new=true");
	}, [navigate, logChat]);

	// Auto-resize textarea - expands to fit content, with scrolling for very long text
	const adjustHeight = useCallback(() => {
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto";

			const scrollHeight = textareaRef.current.scrollHeight;

			// If content is within max height, expand to fit
			if (scrollHeight <= TEXTAREA_MAX_HEIGHT) {
				textareaRef.current.style.height = `${scrollHeight}px`;
				textareaRef.current.style.overflowY = "hidden";
			} else {
				// If content exceeds max height, set to max and enable scrolling
				textareaRef.current.style.height = `${TEXTAREA_MAX_HEIGHT}px`;
				textareaRef.current.style.overflowY = "auto";
			}
		}
	}, []);

	// Re-run height adjustment whenever input changes externally (e.g. pre-filled question from Ask Eve)
	// biome-ignore lint/correctness/useExhaustiveDependencies: input change must trigger DOM height recalc after external setInput calls
	useEffect(() => {
		adjustHeight();
	}, [adjustHeight, input]);

	// Close attach menu when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				attachMenuRef.current &&
				!attachMenuRef.current.contains(event.target as Node)
			) {
				setShowAttachMenu(false);
			}
		};

		if (showAttachMenu) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showAttachMenu]);

	// Convert files to data URLs when files change
	useEffect(() => {
		if (!files || files.length === 0) {
			setFileDataUrls?.([]);
			return;
		}

		const convertFiles = async () => {
			const dataUrls = await convertFilesToDataUrls(files);
			setFileDataUrls?.(dataUrls);
		};

		convertFiles();
	}, [files, setFileDataUrls]);

	const handleInput = (e: ChangeEvent<HTMLTextAreaElement>) => {
		handleInputChange(e);
		adjustHeight();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			if (input?.trim() && !isLoading && !isRecording) {
				// Create a synthetic form event
				const syntheticEvent = {
					preventDefault: () => {},
				} as FormEvent<HTMLFormElement>;
				handleSubmit(syntheticEvent);
			}
		}
	};

	const onSubmit = () => {
		if (input?.trim() && !isLoading && !isRecording) {
			logChat("send_clicked");
			const syntheticEvent = {
				preventDefault: () => {},
			} as FormEvent<HTMLFormElement>;
			handleSubmit(syntheticEvent);
		}
	};

	// Camera handler
	const handleTakePhoto = async () => {
		setShowAttachMenu(false);
		logChat("take_photo_clicked");
		try {
			const image = await Camera.getPhoto({
				quality: 90,
				allowEditing: false,
				resultType: CameraResultType.DataUrl,
				source: CameraSource.Camera,
			});

			if (!image.dataUrl) {
				throw new Error("No image data received");
			}

			// Convert base64 data URL to blob
			const response = await fetch(image.dataUrl);
			const blob = await response.blob();
			const file = new File(
				[blob],
				`photo_${Date.now()}.${image.format || "jpg"}`,
				{
					type: `image/${image.format || "jpeg"}`,
				},
			);

			// Create a new FileList-like object
			const dataTransfer = new DataTransfer();
			dataTransfer.items.add(file);
			setFiles(dataTransfer.files);

			logChat("file_attached", {
				source: "camera",
				file_count: 1,
			});
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to take photo";
			if (!errorMessage.includes("User cancelled")) {
				toast.error(errorMessage);
			}
			console.error("[Camera] Error:", err);
		}
	};

	const handleChooseFromLibrary = async () => {
		setShowAttachMenu(false);
		logChat("photo_library_clicked");
		try {
			const image = await Camera.getPhoto({
				quality: 90,
				allowEditing: false,
				resultType: CameraResultType.DataUrl,
				source: CameraSource.Photos,
			});

			if (!image.dataUrl) {
				throw new Error("No image data received");
			}

			// Convert base64 data URL to blob
			const response = await fetch(image.dataUrl);
			const blob = await response.blob();
			const file = new File(
				[blob],
				`photo_${Date.now()}.${image.format || "jpg"}`,
				{
					type: `image/${image.format || "jpeg"}`,
				},
			);

			// Create a new FileList-like object
			const dataTransfer = new DataTransfer();
			dataTransfer.items.add(file);
			setFiles(dataTransfer.files);

			logChat("file_attached", {
				source: "library",
				file_count: 1,
			});
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to select photo";
			if (!errorMessage.includes("User cancelled")) {
				toast.error(errorMessage);
			}
			console.error("[Photo Library] Error:", err);
		}
	};

	// File upload handlers
	const handleFileButtonClick = () => {
		const willOpen = !showAttachMenu;
		setShowAttachMenu(willOpen);
		if (willOpen) {
			logChat("attachment_menu_opened");
		}
	};

	const handleChooseFiles = () => {
		setShowAttachMenu(false);
		logChat("choose_files_clicked");
		fileInputRef.current?.click();
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = e.target.files;
		if (!selectedFiles || selectedFiles.length === 0) return;

		// Validate file types
		const { valid, invalidFiles } = validateFileTypes(selectedFiles);

		if (!valid) {
			toast.error(FILE_TYPE_ERROR_MESSAGE);
			e.target.value = ""; // Reset input
			logChat("file_attached", {
				success: false,
				invalid_count: invalidFiles.length,
			});
			return;
		}

		setFiles?.(selectedFiles);
		logChat("file_attached", {
			file_count: selectedFiles.length,
		});
	};

	const handleRemoveFile = (index: number) => {
		if (!files) return;

		const newFileList = removeFileFromFileList(files, index);
		setFiles?.(newFileList);

		// Reset input if no files
		if (fileInputRef.current && !newFileList) {
			fileInputRef.current.value = "";
		}

		logChat("file_removed");
	};

	// Voice recording handler using Capacitor Voice Recorder plugin
	const handleMicClick = async () => {
		if (isRecording) {
			// Stop recording
			setIsRecording(false);
			setIsTranscribing(true);
			logChat("voice_recording_stopped");

			try {
				// Stop recording and get the result
				const result = await VoiceRecorder.stopRecording();
				const recordDataBase64 = result.value?.recordDataBase64;

				if (!recordDataBase64) {
					throw new Error("No recording data received");
				}

				// Convert base64 to blob
				const base64Data = recordDataBase64;
				const byteCharacters = atob(base64Data);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const audioBlob = new Blob([byteArray], {
					type: result.value?.mimeType ?? AUDIO_MIME_TYPE,
				});

				// Validate audio blob
				if (audioBlob.size === 0) {
					throw new Error("Recording is empty. Please try again.");
				}

				if (audioBlob.size < 100) {
					throw new Error(
						"Recording is too short. Please speak for at least 1 second.",
					);
				}

				// Send to transcription API with timeout
				const formData = new FormData();
				// Use correct file extension based on mime type
				// iOS returns audio/aac, web returns audio/webm
				const fileExtension = audioBlob.type.includes("aac") ? "m4a" : "webm";
				const fileName = `audio.${fileExtension}`;
				formData.append("audio", audioBlob, fileName);

				// Create abort controller for timeout
				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

				try {
					const response = await api.post("/api/chat/transcribe", formData, {
						headers: {
							"Content-Type": "multipart/form-data",
						},
						signal: controller.signal,
					});

					clearTimeout(timeoutId);

					if (!response.data?.text) {
						throw new Error(
							response.data?.error ||
								"No transcription text received from server",
						);
					}

					setInput(response.data.text);
					logChat("voice_transcribed", {
						text_length: response.data.text.length,
					});
				} catch (apiError) {
					clearTimeout(timeoutId);
					if (
						(apiError as Error).name === "AbortError" ||
						(apiError as { code?: string }).code === "ECONNABORTED"
					) {
						throw new Error(
							"Transcription timed out. Please check your connection and try again.",
						);
					}
					throw apiError;
				}
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : "Transcription failed";
				toast.error(errorMessage);
				console.error("[Voice Recording] Transcription error:", err);
				logChat("voice_transcribed", {
					success: false,
					error: errorMessage,
				});
			} finally {
				setIsTranscribing(false);
			}
		} else {
			// Start recording
			onFocus?.();
			try {
				// Check if device can record
				const canRecord = await VoiceRecorder.canDeviceVoiceRecord();

				if (!canRecord.value) {
					toast.error(tChat("toast.deviceNotSupported"));
					return;
				}

				// Request permission
				const hasPermission =
					await VoiceRecorder.requestAudioRecordingPermission();

				if (!hasPermission.value) {
					toast.error(
						"Microphone permission denied. Please enable it in settings.",
					);
					return;
				}

				setIsRecording(true);
				setInput("");
				logChat("voice_recording_started");

				// Start recording
				await VoiceRecorder.startRecording();
			} catch (err) {
				toast.error(tChat("toast.couldNotStartRecording"));
				setIsRecording(false);
				console.error("[Voice Recording] Start recording error:", err);
			}
		}
	};

	return (
		<div
			className={`${styles.container} ${isRecording ? styles.recording : ""} ${isExpanded ? styles.expanded : ""}`}
		>
			{/* Hidden file input */}
			<input
				ref={fileInputRef}
				type="file"
				accept={ALLOWED_FILE_EXTENSIONS}
				multiple
				onChange={handleFileChange}
				className={styles.fileInput}
			/>

			{/* File previews */}
			{fileDataUrls.length > 0 && (
				<div className={styles.filePreviewContainer}>
					{fileDataUrls.map((item, index) => (
						<FilePreview
							key={`${item.file.name}-${index}`}
							file={item.file}
							dataUrl={item.dataUrl}
							onRemove={() => handleRemoveFile(index)}
						/>
					))}
				</div>
			)}

			<div className={styles.textareaWrapper}>
				<textarea
					ref={textareaRef}
					value={input}
					onChange={handleInput}
					onKeyDown={handleKeyDown}
					onFocus={onFocus}
					onBlur={onBlur}
					placeholder={isTranscribing ? "" : t("chat.placeholder")}
					disabled={isLoading || isRecording || isTranscribing}
					rows={1}
					className={`${styles.textarea} ${isTranscribing ? styles.transcribing : ""}`}
				/>
				{isTranscribing && (
					<div className={styles.transcribingIndicator}>
						<div className={styles.dot} style={{ animationDelay: "0ms" }} />
						<div className={styles.dot} style={{ animationDelay: "200ms" }} />
						<div className={styles.dot} style={{ animationDelay: "400ms" }} />
					</div>
				)}
			</div>

			{isRecording && (
				<div className={styles.recordingText}>
					<span className={styles.recordingDot}>●</span>
					{t("chat.recordingOverlay")}
				</div>
			)}

			<div className={styles.actionsBar}>
				<div className={styles.leftActions}>
					<div ref={attachMenuRef} className={styles.attachMenuWrapper}>
						<button
							type="button"
							className={styles.iconButton}
							onClick={handleFileButtonClick}
							disabled={isLoading || isRecording || isTranscribing}
							aria-label="Add attachment"
						>
							<PlusIcon size={14} />
						</button>
						{showAttachMenu && (
							<div className={styles.attachMenu}>
								{/* Camera options only on native mobile platforms */}
								{isNativePlatform && (
									<>
										<button
											type="button"
											onClick={handleChooseFromLibrary}
											className={styles.attachMenuItem}
										>
											{t("chat.attach.photoLibrary")}
										</button>
										<button
											type="button"
											onClick={handleTakePhoto}
											className={styles.attachMenuItem}
										>
											{t("chat.attach.takePhoto")}
										</button>
									</>
								)}
								<button
									type="button"
									onClick={handleChooseFiles}
									className={styles.attachMenuItem}
								>
									{t("chat.attach.chooseFiles")}
								</button>
								<button
									type="button"
									onClick={handleNewConversation}
									className={`${styles.attachMenuItem} ${styles.attachMenuItemSeparated}`}
								>
									{t("chat.attach.newConversation")}
								</button>
							</div>
						)}
					</div>
					<button
						type="button"
						className={styles.suggestButton}
						disabled={isLoading || isRecording || isTranscribing}
						onClick={() => {
							onSuggestClick?.();
							logChat("suggest_clicked");
						}}
					>
						<div className={styles.suggestIcon}>
							<SparklesIcon size={14} />
						</div>
						<span className={styles.buttonText}>{t("chat.suggest")}</span>
					</button>
					<button
						type="button"
						className={`${styles.voiceButton} ${isRecording ? styles.voiceButtonRecording : ""}`}
						onClick={handleMicClick}
						disabled={isLoading || isTranscribing}
						aria-pressed={isRecording}
						title={isRecording ? "Stop recording" : "Record voice message"}
					>
						<span className={styles.voiceIcon}>
							<MicIcon size={14} />
						</span>
					</button>
				</div>
				<div className={styles.rightActions}>
					{isTranscribing ? (
						<button type="button" disabled className={styles.sendButton}>
							<div className={styles.transcribingDots}>
								<div
									className={styles.dotWhite}
									style={{ animationDelay: "0ms" }}
								/>
								<div
									className={styles.dotWhite}
									style={{ animationDelay: "200ms" }}
								/>
								<div
									className={styles.dotWhite}
									style={{ animationDelay: "400ms" }}
								/>
							</div>
						</button>
					) : (
						<button
							type="button"
							onClick={onSubmit}
							disabled={!input?.trim() || isLoading || isRecording}
							className={styles.sendButton}
						>
							{t("chat.send")}
						</button>
					)}
				</div>
			</div>
		</div>
	);
}
